/**
 * MemoSection.tsx
 * メモ表示 (timeline blocks) + 新規メモ入力
 * ボタン群は廃止。メモ追加は常に「その他」カテゴリで記録。
 */
import React, { useMemo, useState, useCallback } from 'react';
import { SectionProps, MemoBlock } from '../types';
import SectionHeader from '../shared/SectionHeader';
import FieldDisplay from '../shared/FieldDisplay';
import InlineEditField from '../shared/InlineEditField';
import { appendMemoAction } from '../hooks/useMemoTimeline';

interface MemoSectionProps extends SectionProps {
  onOpenMemoAction?: (actionType: string) => void;
  onOpenMemoBlockEdit?: (block: MemoBlock) => void;
}

/** Parse memo text into timeline blocks */
function parseMemoTimeline(memoText: string): MemoBlock[] {
  if (!memoText) return [];
  const lines = memoText.split('\n');
  const items: MemoBlock[] = [];
  let currentBlock: any = null;
  let freeText: string[] = [];

  for (const line of lines) {
    const match = line.match(/^---(.*?) (\d{4}-\d{2}-\d{2} \d{2}:\d{2})---$/);
    if (match) {
      if (freeText.length > 0) {
        const txt = freeText.join('\n').trim();
        if (txt) items.push({ type: 'text', content: txt, originalIdx: items.length });
        freeText = [];
      }
      currentBlock = { type: 'event', title: match[1], time: match[2], lines: [], originalIdx: items.length };
    } else if (currentBlock) {
      if (line === '---') {
        const txt = currentBlock.lines.join('\n').trim();
        items.push({ type: 'event', title: currentBlock.title, time: currentBlock.time, content: txt, originalIdx: currentBlock.originalIdx });
        currentBlock = null;
      } else {
        currentBlock.lines.push(line);
      }
    } else {
      freeText.push(line);
    }
  }
  if (currentBlock) {
    items.push({ type: 'event', title: currentBlock.title, time: currentBlock.time, content: currentBlock.lines.join('\n').trim(), originalIdx: currentBlock.originalIdx });
  } else if (freeText.length > 0) {
    const txt = freeText.join('\n').trim();
    if (txt) items.push({ type: 'text', content: txt, originalIdx: items.length });
  }
  return items;
}

export default function MemoSection({
  data,
  onChange,
  disabled,
  onOpenMemoAction,
  onOpenMemoBlockEdit,
}: MemoSectionProps) {
  const blocks = useMemo(() => parseMemoTimeline(data.memo), [data.memo]);
  const reversedBlocks = useMemo(() => [...blocks].reverse(), [blocks]);
  const [newMemoText, setNewMemoText] = useState('');

  const handleAddMemo = useCallback(() => {
    if (!newMemoText.trim() || !onChange) return;
    const updatedMemo = appendMemoAction(data.memo || '', 'other', newMemoText.trim());
    onChange({ memo: updatedMemo });
    setNewMemoText('');
  }, [newMemoText, data.memo, onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // IME変換中（isComposing）は無視して確定のみ許可
      if (e.nativeEvent.isComposing || e.keyCode === 229) return;
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleAddMemo();
      }
    },
    [handleAddMemo],
  );

  const renderTimeline = () => {
    if (reversedBlocks.length === 0) return <span style={{ color: '#94a3b8' }}>メモなし</span>;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4, width: '100%' }}>
        {reversedBlocks.map((item, idx) => {
          if (item.type === 'text') {
            return (
              <div
                key={idx}
                style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem', cursor: disabled ? 'default' : 'pointer' }}
                onClick={() => !disabled && onOpenMemoBlockEdit?.(item)}
              >
                {item.content}
              </div>
            );
          }
          return (
            <div
              key={idx}
              onClick={() => !disabled && onOpenMemoBlockEdit?.(item)}
              style={{
                borderLeft: '3px solid #3b82f6',
                paddingLeft: 12,
                paddingTop: 4,
                paddingBottom: 4,
                background: '#f8fafc',
                borderRadius: '0 4px 4px 0',
                cursor: disabled ? 'default' : 'pointer',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#334155' }}>{item.title}</span>
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{item.time}</span>
              </div>
              {item.content && (
                <div style={{ fontSize: '0.875rem', color: '#64748b', whiteSpace: 'pre-wrap' }}>
                  {item.content}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <SectionHeader title="メモ" />
      <InlineEditField
        label="ショートメモ"
        field="short_memo"
        value={data.short_memo}
        onChange={onChange}
        disabled={disabled}
        type="textarea"
      />
      <FieldDisplay label="コメント" value={data.comment} />

      {/* 新規メモ入力 */}
      {!disabled && (
        <div className="gc-field" style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <textarea
              value={newMemoText}
              onChange={(e) => setNewMemoText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="メモを入力... (Enter で追加、Shift+Enter で改行)"
              rows={2}
              style={{
                flex: 1,
                padding: '8px 10px',
                fontSize: '0.875rem',
                border: '1px solid #cbd5e1',
                borderRadius: 6,
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
            <button
              type="button"
              onClick={handleAddMemo}
              disabled={!newMemoText.trim()}
              style={{
                padding: '8px 16px',
                fontSize: '0.8rem',
                border: 'none',
                borderRadius: 6,
                cursor: newMemoText.trim() ? 'pointer' : 'default',
                background: newMemoText.trim() ? '#8b5cf6' : '#e2e8f0',
                color: newMemoText.trim() ? '#fff' : '#94a3b8',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              追加
            </button>
          </div>
        </div>
      )}

      {/* タイムライン表示 */}
      <div className="gc-field">
        <label className="gc-label" style={{ fontSize: '0.85rem', color: '#64748b' }}>メモ</label>
        {renderTimeline()}
      </div>
    </>
  );
}
